<script setup>
import { onMounted, ref, computed } from "vue"
import { useRoute } from "vue-router"

import Layout from "../../layouts/main.vue"
import PageHeader from "@/components/page-header"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../state/store"
import useDebouncedFunction from "../../use/useDebouncedFunction"
import { useParseDiscountDetails } from "../../use/useParseDiscountDetails"
import useSortData from "../../use/useSortData"
import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const allstoresales = computed(() => store.state.allStoreSales)

const title = "لیست فروش فروشگاه"
const items = ref([
  {
    text: "فروشگاه ها",
    href: "/dashboard/stores",
  },
  {
    text: "لیست فروش فروشگاه",
    active: true,
  },
])

const orderStatus = ref("")
const search = ref("")
const perPage = ref(10)

const route = useRoute()
const storeId = route.params.storeId

const getAllStoreSales = async (url = null) => {
  try {
    await store.dispatch("getAllStoreSales", {
      storeId,
      url,
      search: search.value,
      order_status: orderStatus.value,
      per_page: perPage.value,
      sort_field: sortField.value,
      sort_direction: sortDirection.value,
    })
  } catch (error) {
    console.error(error)
    return null
  }
}

const { handleInput } = useDebouncedFunction(getAllStoreSales)
const { parseDiscountDetails, formattedPrices } = useParseDiscountDetails()

onMounted(async () => {
  getAllStoreSales()
})

const { sortField, sortDirection, sortData } = useSortData(getAllStoreSales)

const sortAllStoreSales = (field) => {
  sortData(field)
  getAllStoreSales()
}

const statusLabels = {
  PAYING: "در حال پرداخت",
  PENDING: "در انتظار تایید (در حال بررسی)",
  PREPARING_TO_SEND: "آماده سازی برای ارسال",
  DELIVERED: "تحویل داده شده",
  CANCELLED: "لغو شده",
}

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getAllStoreSales(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-row>
      <b-col lg="12">
        <b-card no-body id="storeCreditorDetailList">
          <b-card-header class="border-bottom-dashed">
            <b-row class="g-4 align-items-center">
              <b-col sm>
                <div>
                  <h5 class="card-title mb-0">لیست فروش فروشگاه</h5>
                </div>
              </b-col>
              <b-col sm="auto">
                <div class="d-flex flex-wrap align-items-start gap-2"></div>
              </b-col>
            </b-row>
          </b-card-header>

          <b-card-body class="border-bottom-dashed border-bottom">
            <b-row class="g-3">
              <b-col xl="7">
                <div class="search-box">
                  <input
                    v-model="search"
                    @input="handleInput"
                    type="text"
                    class="form-control search"
                    placeholder="جستجوی نام محصول ..."
                  />
                  <i class="ri-search-line search-icon"></i>
                </div>
              </b-col>

              <b-col xl="5">
                <b-row class="g-3">
                  <b-col sm="6">
                    <select
                      @change="getAllStoreSales(null)"
                      v-model="orderStatus"
                      class="form-select mb-3"
                      aria-label="Default select example"
                    >
                      <option value="" selected>انتخاب وضعیت سفارش</option>
                      <option
                        v-for="(label, value) in statusLabels"
                        :key="value"
                        :value="value"
                      >
                        {{ label }}
                      </option>
                    </select>
                  </b-col>

                  <b-col sm="6">
                    <select
                      @change="getAllStoreSales(null)"
                      v-model="perPage"
                      class="form-select mb-3"
                      aria-label="Default select example"
                    >
                      <option value="" selected>نمایش تعداد ردیف</option>
                      <option value="2">2</option>
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="30">30</option>
                      <option value="50">50</option>
                    </select>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-card-body>
          <b-card-body>
            <div>
              <div class="table-responsive table-card mb-1">
                <table class="table align-middle" id="storeCreditorDetailTable">
                  <thead class="table-light text-muted">
                    <tr>
                      <th class="sort" data-sort="product">جزئیات محصول</th>
                      <TableHeaderCell
                        field="original_price"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        @click="sortAllStoreSales('original_price')"
                      >
                      قیمت
                      </TableHeaderCell>
                      <TableHeaderCell
                        field="quantity"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        @click="sortAllStoreSales('quantity')"
                      >
                      تعداد
                      </TableHeaderCell>
                      <th class="sort" data-sort="discunt">تخفیف</th>
                      <th class="sort" data-sort="order_tracking_code">
                        شماره سفارش
                      </th>
                      <th class="sort" data-sort="order_status">وضعیت</th>
                      <th class="sort" data-sort="price">مبلغ کل</th>

                      <TableHeaderCell
                        field="created_at"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        @click="sortAllStoreSales('created_at')"
                      >
                      زمان ثبت سفارش
                      </TableHeaderCell>
                    </tr>
                  </thead>

                  <tbody
                    class="list"
                    v-if="allstoresales.loading || !allstoresales.data.length"
                  >
                    <tr>
                      <td colspan="8">
                        <div class="text-center">
                          <b-spinner
                            v-if="allstoresales.loading"
                            variant="dark"
                            style="width: 3rem; height: 3rem"
                          ></b-spinner>
                          <div v-else>
                            <lottie
                              class="avatar-xl"
                              colors="primary:#121331,secondary:#08a88a"
                              :options="defaultOptions"
                              :height="75"
                              :width="75"
                            />
                            <h5 class="mt-2">هیچ سفارشی وجود ندارد!</h5>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>

                  <tbody class="list" v-else>
                    <tr
                      v-for="(item, index) of allstoresales.data"
                      :key="index"
                    >
                      <td>
                        <div class="d-flex">
                          <div
                            class="flex-shrink-0 avatar-md bg-light rounded p-1"
                          >
                            <img
                              :src="item.product.image"
                              alt=""
                              class="img-fluid d-block"
                            />
                          </div>
                          <div class="flex-grow-1 ms-3">
                            <h5 class="fs-15">
                              <router-link to="" class="link-primary"
                                >{{ item.product.name }}
                              </router-link>
                            </h5>
                            <p
                              v-for="(value, index) of item.product.values"
                              :key="index"
                              class="text-muted mb-0"
                            >
                              {{ value.attributeName }}:
                              <span class="fw-medium">{{
                                value.valueName
                              }}</span>
                            </p>
                            <p
                              v-if="item.product.createdBy"
                              class="text-muted mb-0 mt-1"
                            >
                              <strong>فروشگاه: </strong>
                              <span class="fw-medium">{{
                                item.product.createdBy
                              }}</span>
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>{{ formattedPrices(item.originalPrice) }}</td>
                      <td>{{ persianTools.digitsEnToFa(item.qty) }}</td>
                      <td>
                        <div v-if="item.discountDetails">
                          <p
                            v-if="typeof item.discountDetails === 'string'"
                            v-html="parseDiscountDetails(item.discountDetails)"
                          ></p>
                          <p v-else>
                            <strong>نوع تخفیف:</strong>
                            {{ item.discountDetails.type }}<br />
                            <strong>مقدار تخفیف:</strong>
                            {{ item.discountDetails.amount }}<br />
                            <strong>درصد تخفیف:</strong>
                            {{ item.discountDetails.percent }}<br />
                            <strong>قیمت با تخفیف:</strong>
                            {{ item.discountDetails.priceWithDiscount }}
                          </p>
                        </div>

                        <!-- نمایش پیغام در صورتی که تخفیف موجود نباشد -->
                        <p v-else>تخفیف موجود نیست.</p>
                      </td>
                      <td>
                        <router-link
                          :to="{
                            name: 'orders.details',
                            params: { id: item.orderId },
                          }"
                          class="link-primary"
                        >
                          {{ persianTools.digitsEnToFa(item.trackingCode) }}
                        </router-link>
                      </td>
                      <td class="status">
                        <span
                          class="badge text-uppercase"
                          :class="{
                            'badge-soft-warning': item.orderStatus == 'PAYING',
                            'badge-soft-info': item.orderStatus == 'PENDING',
                            'badge-soft-primary':
                              item.orderStatus == 'PREPARING_TO_SEND',
                            'badge-soft-success':
                              item.orderStatus == 'DELIVERED',
                            'badge-soft-danger':
                              item.orderStatus == 'CANCELLED',
                          }"
                          >{{ statusLabels[item.orderStatus] }}</span
                        >
                      </td>
                      <td class="fw-medium">
                        {{ formattedPrices(item.price) }}
                      </td>
                      <td class="create_at">
                        {{ toPersianDateTime(item.createOrderDateTime) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                class="d-flex justify-content-end"
                v-if="allstoresales.total > allstoresales.limit"
              >
                <div class="pagination-wrap hstack gap-2" style="display: flex">
                  <ul class="pagination listjs-pagination mb-0">
                    <li
                      v-for="(link, i) of allstoresales.links"
                      :key="i"
                      class="page-item"
                      :class="{
                        disabled: !link.url,
                        active: link.active || !link.url,
                      }"
                    >
                      <span
                        class="page-link"
                        v-if="link.active"
                        v-html="link.label"
                      ></span>

                      <a
                        v-else
                        href="#"
                        @click="getForPage($event, link)"
                        class="page-link"
                        v-html="link.label"
                      >
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>
